// Text props that can't be animated: they live on the clip (animation.values)
// and never on individual keyframes.
export const TEXT_CLIP_STYLE_PROPS = ['font.family', 'font.style', 'font.align']

const TEXT_CLIP_STYLE_PROP_SET = new Set(TEXT_CLIP_STYLE_PROPS)
const KEYFRAME_PROP_MAPS = ['props', 'delay', 'easing', 'interpOut']

export const isTextClipStyleProp = (code) => TEXT_CLIP_STYLE_PROP_SET.has(code)

export function splitTextClipStyleUpdate(update = {}) {
  const clip = {}
  const rest = {}
  Object.keys(update || {}).forEach((code) => {
    if (isTextClipStyleProp(code)) clip[code] = update[code]
    else rest[code] = update[code]
  })
  return { clip, rest }
}

export function keyframeHasTextClipStyle(kf, codes = TEXT_CLIP_STYLE_PROPS) {
  const props = kf?.props
  if (!props || typeof props !== 'object') return false
  return codes.some((code) => typeof props[code] !== 'undefined')
}

export function removeTextClipStyleFromKeyframe(kf, codes = TEXT_CLIP_STYLE_PROPS) {
  if (!kf || typeof kf !== 'object') return kf
  const touched = KEYFRAME_PROP_MAPS.some((key) => {
    const map = kf[key]
    return map && typeof map === 'object' && codes.some((code) => code in map)
  })
  if (!touched) return kf
  const next = { ...kf }
  KEYFRAME_PROP_MAPS.forEach((key) => {
    const map = kf[key]
    if (!map || typeof map !== 'object') return
    const copy = { ...map }
    codes.forEach((code) => { delete copy[code] })
    next[key] = copy
  })
  return next
}

export function applyTextClipStyle(animation, clip = {}) {
  if (!animation || typeof animation !== 'object') return false
  const codes = Object.keys(clip).filter(isTextClipStyleProp)
  if (!codes.length) return false
  animation.values = { ...(animation.values || {}), ...clip }
  if (Array.isArray(animation.keyframes)) {
    animation.keyframes = animation.keyframes.map((kf) => removeTextClipStyleFromKeyframe(kf, codes))
  }
  return true
}

// Moves clip-style props found on keyframes into animation.values. The earliest
// keyframe wins because that is what the clip displayed at its start.
export function migrateTextClipStyle(animation) {
  if (!animation || !Array.isArray(animation.keyframes)) return false
  if (!animation.keyframes.some((kf) => keyframeHasTextClipStyle(kf))) return false
  const sorted = [...animation.keyframes].sort((a, b) => (a?.time || 0) - (b?.time || 0))
  const clip = {}
  TEXT_CLIP_STYLE_PROPS.forEach((code) => {
    const source = sorted.find((kf) => typeof kf?.props?.[code] !== 'undefined')
    if (source) clip[code] = source.props[code]
  })
  return applyTextClipStyle(animation, clip)
}

export function describeTextClipStyle(strip) {
  const animation = strip?.animation || {}
  const pick = (props = {}) => TEXT_CLIP_STYLE_PROPS.reduce((acc, code) => {
    if (typeof props?.[code] !== 'undefined') acc[code] = props[code]
    return acc
  }, {})
  return {
    clip: pick(animation.values),
    keyframes: (animation.keyframes || []).map((kf) => ({
      time: kf?.time,
      type: kf?.type,
      ...pick(kf?.props),
    })),
  }
}
